import { Button, Flex, Select, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { IconPlus } from '@tabler/icons-react';
import { ComponentProps } from 'react';
import ModalBox from 'src/UI/common/ModalBox';
import { z } from 'zod';
import CreateClientForm from 'src/clients/CreateClientForm';
import { useClients } from 'src/clients/hooks/useClients';

const formSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  emailAddress: z.string().email().nullable().optional(),
  advised: z.boolean(),
});

export type AddManuallyFormValues = {
  firstName: string;
  lastName: string;
  emailAddress: string | null | undefined;
  advised: boolean;
};

type Props = {
  onSubmit: (values: AddManuallyFormValues) => void;
} & Partial<ComponentProps<typeof ModalBox>>;

export function AddManuallyView({ onSubmit, onClose, ...props }: Props) {
  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      emailAddress: '',
      advised: true,
    },
    validate: values => {
      const zodErrors = zodResolver(formSchema)(values);

      return {
        ...zodErrors,
        emailAddress: !values.emailAddress
          ? null
          : isDuplicateEmail(values.emailAddress)
            ? 'This email address is already in use.'
            : zodErrors.emailAddress,
      };
    },
    validateInputOnBlur: true,
  });

  const { clients } = useClients();

  const isDuplicateEmail = (emailAddress: string) => {
    return clients.some(
      client => client.emailAddress?.toLowerCase() === emailAddress.toLowerCase(),
    );
  };

  const handleSubmit = (values: AddManuallyFormValues) => {
    onSubmit(values);
    form.reset();
  };

  return (
    <ModalBox title="Add manually" onClose={onClose} {...props}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Flex direction="column" gap="md">
          <Text fw={600} size="md" c="gray.10">
            Client details
          </Text>

          <CreateClientForm form={form} />
          {/*Having typing issues with this select in the CreateClientForm so I extracted it and duplicated it*/}
          <Select
            label="Is this person a client?"
            placeholder="Please select"
            data={[
              { value: 'true', label: 'Yes, they are an advised client' },
              { value: 'false', label: 'No, they are a contact' },
            ]}
            onChange={value => form.setFieldValue('advised', value === 'true')}
            value={form.values.advised?.toString()}
          />

          <Flex justify="flex-start">
            <Button
              type="submit"
              variant="outline-black"
              leftSection={<IconPlus size={16} />}
              disabled={!form.isValid()}
            >
              Add to family
            </Button>
          </Flex>
        </Flex>
      </form>
    </ModalBox>
  );
}
